import React from 'react';

import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {spacing} from '@nfq/react-grid';

import {P} from '../../layout/Text';

import {useHoverTooltip} from './useHoverTooltip';

import type {WithChild} from '../../../utils/childTypes';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 * This interface ensures type safety and provides clear documentation for all available configuration options.
 */
export interface ComponentProps {
    /**
     * The className to be applied to the wrapper element.
     * This property allows for custom styling of the tooltip wrapper component.
     * It can be used to override default styles or add additional CSS classes for specific use cases.
     */
    className?: string;
    /**
     * The content to be displayed in the tooltip.
     * This string represents the text that will appear inside the tooltip when it becomes visible on hover.
     * The content should be concise and informative to provide meaningful context to the user.
     */
    content: string;
    /**
     * A boolean flag to disable the tooltip. If `true`, the tooltip is not rendered.
     * When disabled, the component will still render its children but without any tooltip functionality.
     * This is useful for conditionally showing tooltips based on application state or user preferences.
     *
     * @default false
     */
    isDisabled?: boolean;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     * The identifier is applied to the wrapper element's data-cy attribute for easy selection in automated testing frameworks.
     *
     * @default 'HoverTooltip'
     */
    testId?: string;
}

/**
 * A React component that renders a hover-triggered tooltip for its children elements.
 * The tooltip displays contextual information when users hover over the wrapped content, providing a non-intrusive way to show additional details.
 * The component manages positioning, visibility states, and smooth transitions for an optimal user experience.
 *
 * @param props            The component props.
 * @param props.children   The child elements that will trigger the tooltip on hover.
 * @param props.className  Optional CSS class name to apply custom styling to the wrapper element.
 * @param props.content    The text content to display inside the tooltip when it becomes visible.
 * @param props.isDisabled Optional flag to disable tooltip functionality while preserving child rendering.
 * @param props.testId     Optional test identifier for automated testing frameworks.
 * @returns A JSX element containing the wrapped children with hover tooltip functionality.
 *
 * @example
 * ```tsx
 * <HoverTooltip content="This is helpful information" testId="help-tooltip">
 *   <button>Hover me</button>
 * </HoverTooltip>
 * ```
 */
const HoverTooltip = ({
    children,
    className,
    content,
    isDisabled = false,
    testId = 'HoverTooltip'
}: WithChild<ComponentProps>) => {
    const {
        colors,
        handleMouseEnter,
        handleMouseLeave,
        isTooltipVisible,
        showTooltipContent,
        tooltipRef,
        tooltipWrapperRef
    } = useHoverTooltip();

    return isDisabled ? <Wrapper className={className}>{children}</Wrapper> : (
        <Wrapper
            ref={tooltipWrapperRef}
            className={className}
            data-cy={testId}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {isTooltipVisible && (
                <Tooltip
                    ref={tooltipRef}
                    $showTooltipContent={showTooltipContent}
                >
                    <P><Color $color={colors.neutral0}>{content}</Color></P>
                </Tooltip>
            )}
            {children}
        </Wrapper>
    );
};

HoverTooltip.displayName = 'HoverTooltip';

export {HoverTooltip};

const Wrapper = styled.div`
    position: relative;
`;

interface TooltipProps {
    $showTooltipContent: boolean;
}

const Tooltip = styled.div<TooltipProps>`
    align-items: center;
    background-color: ${({theme}) => theme.colors.neutral80};
    border-radius: 4px;
    box-shadow: ${({theme}) => theme.boxShadows.popOverShadow};
    display: inline-flex;
    justify-content: center;
    opacity: ${({$showTooltipContent}) => ($showTooltipContent ? 1 : 0)};
    padding: ${spacing(2)};
    position: fixed;
    transition: opacity 0.2s ease-in-out;
    width: max-content;
    z-index: 100;
`;